<template>
  <div class="col-grow relative-position">
    <!-- Combinations table (tabulator) -->
    <div ref="combinationsTable" class="mi-tabulator compact"></div>

    <!-- Data loading spinner -->
    <mi-inner-loading class="bg-white" :showing="combinationsOptions.isCombinationsTableLoading">
      <mi-spinner size="70px"></mi-spinner>
      <h6 class="q-mt-lg q-mb-none"> {{ combinationsOptions.combinationsTableLoadingContent }} </h6>
    </mi-inner-loading>
  </div>
</template>

<script>
  import Tabulator from 'tabulator-tables'
  import { useStore } from 'vuex'

  import {
    getVariantTreeTableHeader,
    swapVariantTreeTableColumns
  } from '@/api'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'

  import { COMBINATIONS_STATES, SEARCH_RESULT_TYPES } from '@/constants'
  import { VARIANT_TREE_TABLE_CONTENT } from '@/api/rest/variant-tree/endpoints'
  import { VM_SWAP_COLUMN } from '@/utils/analytics/constants'

  import { computed, onMounted, ref, watch } from 'vue'
  import notify from '@/utils/notify'
  import { useAmbiguousDetails } from '@/components/combinations/combinations-table/utils/useAmbiguousDetails'
  import { useCombinationsOptions } from '../utils/composables/useCombinationsOptions'

  const FROZEN_FIELD_NAME = 'rowNumber'

  const CSS_COL_CLASS = 'mi-tabulator__header-cell'
  const CSS_ROW_CLASS = 'mi-tabulator__header-cell--row-number'

  export default {
    name: 'CombinationsTable',
    props: {
      correlationId: {
        type: String,
        required: true
      },
      showResults: {
        type: Object,
        required: true,
        default: () => {}
      }
    },
    setup(props) {
      const isSelectionDialogShown = ref(false)
      const tabulator = ref({})
      const tableHeader = ref([])
      const tableRows = ref([])
      const rowSelectedOptions = ref([])
      const rowSelectedComponents = ref([])
      const store = useStore()
      const combinationsTable = ref(null)
      const hasAmbiguousRows = ref(false)
      const ambiguousFields = ref(new Set())

      const resultsFilter = computed(() => store.state.search.resultsFilter)

      const { aliasesAsRequestParams, combinationsOptions } = useCombinationsOptions()

      const clearAmbiguousActionCell = row => {
        const rowEl = row.getElement()

        rowEl.querySelectorAll('.ambiguous-cell-with-action').forEach(el => {
          const originalValue = el.dataset.originalValue || ''
          el.innerHTML = originalValue
          el.classList.remove('ambiguous-cell-with-action')
          delete el.dataset.originalValue
        })
      }

      const refreshAmbiguousRow = row => {
        row.reformat()
        row.normalizeHeight()
      }

      const {
        getAmbiguousRowState,
        handleCheckDetailsClick,
        getHeaderColumnFromCell
      } = useAmbiguousDetails({
        tableHeader
      })

      const addAmbiguousActionCell = row => {
        const cells = row.getCells?.() || []
        const rowData = row.getData()
        const rowState = getAmbiguousRowState(rowData.id)

        const ambiguousCell = cells.find(cell => {
          const value = cell.getValue?.()

          if (typeof value !== 'string' || !value.includes('Ambiguous')) {
            return false
          }

          const headerColumn = getHeaderColumnFromCell(cell)

          return headerColumn?.type === 'component'
        })

        if (!ambiguousCell) return

        const cellEl = ambiguousCell.getElement()

        if (cellEl.classList.contains('ambiguous-cell-with-action')) return

        const currentValue = cellEl.textContent?.trim() || ambiguousCell.getValue?.() || ''
        const isPersistent = rowState.isChecking

        let buttonLabel = ''
        if (rowState.isChecking) {
          buttonLabel = '<span class="check-details-btn__spinner"></span><span>Checking...</span>'
        }
        else if (rowState.isExpanded) {
          buttonLabel = '<span>Hide details</span>'
        }
        else {
          buttonLabel = '<span>Check Details</span>'
        }

        const detailsHtml = rowState.isExpanded
          ? rowState.details.map(detail => `
      <div class="ambiguous-cell-details__item">
        <span class="ambiguous-cell-details__value" title="${ detail }">${ detail }</span>
      </div>
    `).join('')
          : ''

        cellEl.dataset.originalValue = currentValue
        cellEl.classList.add('ambiguous-cell-with-action')

        cellEl.innerHTML = `
    <div class="ambiguous-cell-layout ambiguous-cell-layout--${ rowState.isExpanded ? 'expanded' : 'collapsed' }">
      <div class="ambiguous-cell-layout__top">
        <div class="ambiguous-cell-layout__label">${ currentValue }</div>
        <div class="ambiguous-cell-layout__action ${ isPersistent ? 'ambiguous-cell-layout__action--visible' : '' }">
          <button
            type="button"
            class="check-details-btn ${ rowState.isChecking ? 'check-details-btn--loading' : '' }"
          >
            ${ buttonLabel }
          </button>
        </div>
      </div>

      ${ rowState.isExpanded ? `
        <div class="ambiguous-cell-details">
          ${ detailsHtml }
        </div>
      ` : '' }
    </div>
  `

        const button = cellEl.querySelector('.check-details-btn')

        if (button) {
          button.addEventListener('click', event => {
            event.stopPropagation()
            handleCheckDetailsClick({
              row,
              cell: ambiguousCell,
              refreshRow: refreshAmbiguousRow
            })
          })
        }
      }

      const isYellowWorld = (type, onlyHeader = false) => {
        const pluralType = `${ type }s`
        const { OPTIONS, CHOICES } = SEARCH_RESULT_TYPES
        return onlyHeader ? [OPTIONS].includes(pluralType) : [OPTIONS, CHOICES].includes(pluralType)
      }

      const isGreenWorld = (type, onlyHeader = false) => {
        const pluralType = `${ type }s`
        const { COMPONENTS, COMPONENT_VARIANTS } = SEARCH_RESULT_TYPES
        return onlyHeader ? [COMPONENTS].includes(pluralType) : [COMPONENTS, COMPONENT_VARIANTS].includes(pluralType)
      }

      const isSolutionElement = type => {
        const pluralType = `${ type }s`
        const { SOLUTION_ELEMENTS } = SEARCH_RESULT_TYPES
        return [SOLUTION_ELEMENTS].includes(pluralType)
      }

      const getHeaderColumnCssClass = (type = '') => {
        let cssClass = CSS_COL_CLASS

        if (isYellowWorld(type)) {
          cssClass += ` ${ CSS_COL_CLASS }--yellow`
        }
        else if (isGreenWorld(type)) {
          cssClass += ` ${ CSS_COL_CLASS }--green`
        }
        else if (isSolutionElement(type, true)) {
          cssClass += ` ${ CSS_COL_CLASS }--blue`
        }

        return cssClass
      }

      const updateTableHeaderAfterColumnMoved = (columns = []) => {
        const newTableHeader = []
        let hasHeaderChanged = false

        columns
          .filter(({ _column } = {}) => _column.field !== FROZEN_FIELD_NAME)
          .forEach(({ _column } = {}, columnIdx) => {
            const headerItemIdx = tableHeader.value.findIndex(
              ({ field } = {}) => field === _column.field?.replace(/-/g, '.')
            )

            hasHeaderChanged = headerItemIdx !== columnIdx

            if (headerItemIdx > -1) {
              newTableHeader.push(tableHeader.value[headerItemIdx])
            }
          })

        return { newTableHeader, hasHeaderChanged }
      }

      const getCellLabel = ({ name = '', codeId = '', type = '' } = {}, showTitle = true) => {
        const { showCodeId, showName } = resultsFilter.value

        let title = ''

        if (isYellowWorld(type, true)) {
          title += '<p class="mi-tabulator-header-info-title">Choices for <p/>'
        }
        else if (isGreenWorld(type, true)) {
          title += '<p class="mi-tabulator-header-info-title">Component Variants for <p/>'
        }
        else if (isSolutionElement(type)) {
          title += '<p class="mi-tabulator-header-info-title">Solution elements for <p/>'
        }

        // eslint-disable-next-line max-len,vue/max-len
        return `${ showTitle ? title : '' } ${ showCodeId ? codeId : '' }${ showCodeId && showName ? ' - ' : '' }${ showName ? name : '' }`
      }

      const getHeaderColumns = () => {
        const col = [{
          formatter: 'rownum',
          field: FROZEN_FIELD_NAME,
          cssClass: `${ CSS_COL_CLASS } ${ CSS_ROW_CLASS }`,
          minWidth: 34,
          width: 34,
          resizable: false,
          headerSort: false,
          frozen: true
        }]

        const cols = tableHeader.value.map(({ name, codeId, field, type }) => {
          const normalizedField = field.replace(/\./g, '-')
          const isAmbiguousField = ambiguousFields.value.has(normalizedField)

          return {
            title: getCellLabel({ name, codeId, type }),
            field: normalizedField,
            headerFilter: true,
            headerFilterPlaceholder: 'Search',
            cssClass: getHeaderColumnCssClass(type),
            minWidth: isAmbiguousField ? 300 : 60
          }
        })

        return col.concat(cols)
      }

      const hasAnyResultTypeSelected = computed(() => Object.values(props.showResults).some(v => v === true))

      const getPlaceholderHtml = message => `
      <div class="mi-tabulator__empty">
        <i class="mi-icon mi-icon--warning-circle"></i>
        <div class="mi-tabulator__empty-title">${ message }</div>
      </div>`

      const placeHolderMessage = computed(() => {
        if (!hasAnyResultTypeSelected.value) {
          return 'No result types selected. Enable at least one filter to view results.'
        }
        return 'No combinations found.'
      })

      const applyPlaceholder = message => {
        const el = combinationsTable.value
        if (!el) return
        const placeholderEl = el.querySelector('.tabulator-placeholder')
        if (placeholderEl) placeholderEl.innerHTML = getPlaceholderHtml(message)
      }

      const setTabulatorData = async (setSorts = true) => {
        ambiguousFields.value = new Set()
        hasAmbiguousRows.value = false
        const { showCodeId, showName } = resultsFilter.value
        const endpointUrl = VARIANT_TREE_TABLE_CONTENT.replace(':correlationId', props.correlationId)
        const tableContentUrl = `${ process.env.VUE_APP_DOMAIN_URL }${ process.env.VUE_APP_VM_URL }${ endpointUrl }`
        const headers = {
          Authorization: `Bearer ${
            localStorage.getItem(process.env.VUE_APP_STORAGE_KEY_TOKEN)?.replace(/^__q_strn\|/, '') || ''
          }`
        }

        combinationsOptions.isCombinationsTableLoading = true

        try {
          await tabulator.value.setData(
            tableContentUrl,
            {
              ...aliasesAsRequestParams(),
              showCodeId,
              showName,
              showAmbiguous: props.showResults.ambiguous,
              showIncomplete: props.showResults.incomplete,
              showValidResults: props.showResults.valid
            },
            { headers }
          )

          tabulator.value.setColumns(getHeaderColumns())
          if (setSorts) tabulator.value.setSort(combinationsOptions.appliedSorters)
          combinationsOptions.appliedFilters.forEach(({ field, value } = {}) => {
            tabulator.value.setHeaderFilterValue(field, value)
          })
          if (tabulator.value.redraw) tabulator.value.redraw(true)
          applyPlaceholder(placeHolderMessage.value)
        }
        finally {
          combinationsOptions.isCombinationsTableLoading = false
        }
      }

      const swapColumns = async (column, columns = []) => {
        combinationsOptions.combinationsTableLoadingContent = COMBINATIONS_STATES.REARRANGING

        const { newTableHeader, hasHeaderChanged } = updateTableHeaderAfterColumnMoved(columns)

        if (!hasHeaderChanged) return

        const { showCodeId, showName } = resultsFilter.value

        tableHeader.value = newTableHeader

        await swapVariantTreeTableColumns({
          showCodeId,
          showName,
          correlationId: props.correlationId,
          header: tableHeader.value
        })

        await setTabulatorData()

        recordAnalytics(VM_SWAP_COLUMN)
      }

      const updateFilters = () => {
        if (!tabulator.value.getHeaderFilters) return

        const tabulatorFilters = tabulator.value.getHeaderFilters?.() || []
        combinationsOptions.appliedFilters = tabulatorFilters?.map(({ field, value } = {}) => ({ field, value }))
      }

      const updateSorters = (tabulatorSorters = []) => {
        if (!(combinationsOptions.appliedSorters.length || tabulatorSorters?.length)) return
        combinationsOptions.appliedSorters = tabulatorSorters?.map(
          ({ field, dir } = {}) => ({ column: field, dir })
        )
      }

      const getRowPrecondition = (row = {}) => {
        const rowCells = row?.cells || []

        const choices = rowCells.filter(cell => cell.type === 'choice')

        return [...choices.flatMap(choice => choice.codeId)]
      }

      const formatResponseForTabulator = ({ lastPage, rows = [] } = {}) => {
        const startIndex = tabulator.value.getDataCount?.() || 0

        if (lastPage > 1) {
          tableRows.value.push(...rows)
        }
        else {
          tableRows.value = rows
        }

        const data = rows.map((row, index) => {
          const result = {
            id: startIndex + index + 1,
            combinationId: props.correlationId,
            precondition: getRowPrecondition(row),
            bomValidationStatus: row.bomValidationStatus || null,
            bomValidationCorrelationId: row.bomValidationCorrelationId || null
          }

          row?.cells.forEach((cell, idx) => {
            const { field } = tableHeader.value[idx]
            const normalizedField = field.replace(/\./g, '-')

            result[normalizedField] = getCellLabel(cell, false)

            if (cell.name === 'Ambiguous') {
              ambiguousFields.value.add(normalizedField)
            }

            if (cell.bomValidationStatus) {
              result.bomValidationStatus = cell.bomValidationStatus
            }

            if (cell.bomValidationCorrelationId) {
              result.bomValidationCorrelationId = cell.bomValidationCorrelationId
            }

            result.isAmbiguous = row.cells.some(rowCell => rowCell.name === 'Ambiguous')
            result.isIncomplete = row.cells.some(rowCell => rowCell.name === 'Incomplete')
          })

          if (!hasAmbiguousRows.value && result.isAmbiguous) {
            hasAmbiguousRows.value = true
          }

          return result
        })

        return { last_page: lastPage, data }
      }

      const setTabulatorHeader = async () => {
        const { header } = await getVariantTreeTableHeader({
          aliases: aliasesAsRequestParams(),
          correlationId: props.correlationId
        })

        tableHeader.value = header
      }

      const initTabulator = async () => {
        combinationsOptions.combinationsTableLoadingContent = COMBINATIONS_STATES.LOADING

        tabulator.value = new Tabulator(combinationsTable.value, {
          ajaxFiltering: true,
          ajaxSorting: true,
          ajaxProgressiveLoad: 'scroll',
          columnMinWidth: 60,
          height: '100%',
          maxHeight: '100%',
          layout: 'fitColumns',
          movableColumns: true,
          tooltips: true,
          headerSortTristate: true,
          paginationSize: 50,
          selectable: 'highlight',
          rowClick: false,
          placeholder: getPlaceholderHtml(placeHolderMessage.value),
          dataSorted: updateSorters,
          dataFiltered: updateFilters,
          columnMoved: swapColumns,
          ajaxResponse: (url, params, response) => formatResponseForTabulator(response),
          rowFormatter(row) {
            const rowData = row.getData()
            const rowEl = row.getElement()

            rowEl.classList.remove('tabulator-row--ambiguous', 'tabulator-row--incomplete')

            clearAmbiguousActionCell(row)

            if (rowData.isAmbiguous) {
              rowEl.classList.add('tabulator-row--ambiguous')
              addAmbiguousActionCell(row)
            }
            else if (rowData.isIncomplete) {
              rowEl.classList.add('tabulator-row--incomplete')
            }
          }
        })

        await setTabulatorHeader()
        tabulator.value.setColumns(getHeaderColumns())
        await setTabulatorData()
      }

      const refreshTabulator = async () => {
        combinationsOptions.appliedFilters = []
        combinationsOptions.appliedSorters = []

        await setTabulatorHeader()
        await setTabulatorData()
      }

      watch(
        () => combinationsOptions.appliedAliasesIds,
        () => {
          refreshTabulator()
        },
        { deep: true }
      )

      onMounted(async () => {
        await initTabulator()

        if (hasAmbiguousRows.value) {
          notify({
            title: 'Place the cursor over an ‘Ambiguous’ row to check details.',
            type: 'info',
            color: 'primary',
            progress: false,
            position: 'bottom-right',
            timeout: 2000,
            closeIcon: true
          })
        }

        watch(
          () => [
            resultsFilter.value.showCodeId,
            resultsFilter.value.showName,
            props.showResults.ambiguous,
            props.showResults.incomplete,
            props.showResults.valid
          ],
          () => {
            setTabulatorData(false)
          },
          { deep: true }
        )
      })

      return {
        isSelectionDialogShown,
        rowSelectedComponents,
        rowSelectedOptions,
        combinationsOptions,
        tableRows,
        combinationsTable,
        tableHeader,
        tabulator,
        setTabulatorData,
        swapColumns
      }
    }
  }
</script>

<style lang="scss">
  //stylelint-disable declaration-no-important
  @import "@/styles/tabulator";

  p {
    margin: 0;
  }

  .mi-tabulator-header-info-title {
    font-family: "MAN Europe Condensed", sans-serif;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    text-align: left;
  }

  .tabulator-placeholder {
    width: 100%;
    justify-content: center;
    font-size: 16px;
  }

  .tabulator-row--ambiguous {
    background-color: #fde3e8 !important;
  }

  .tabulator-row--ambiguous:hover .tabulator-cell {
    background-color: $mi-red-100 !important;
  }

  .tabulator-row--incomplete {
    background-color: #fff8e0 !important;
  }

  .ambiguous-cell-with-action {
    padding: 0 !important;
    vertical-align: top;
  }

  .ambiguous-cell-layout {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100%;
  }

  .ambiguous-cell-layout__top {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: stretch;
    width: 100%;
  }

  .ambiguous-cell-layout__label {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ambiguous-cell-layout__action {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 200ms ease;

    .check-details-btn {
      background-color: $mi-red-50 !important;
    }

    .check-details-btn:hover {
      background-color: $mi-red-100 !important;
    }
  }

  .ambiguous-cell-layout__action--visible {
    opacity: 1 !important;
    pointer-events: auto;
  }

  .tabulator-row--ambiguous:hover .ambiguous-cell-layout__action {
    opacity: 1;
    pointer-events: auto;
  }

  .check-details-btn {
    border: 1px solid $mi-anthracite-900;
    background: $mi-red-100;
    padding: 4px 8px;
    font-size: 12px;
    line-height: 16px;
    cursor: pointer;
    font-weight: 700;
    font-family: "MAN Europe Condensed", sans-serif;
    white-space: nowrap;
    background-color: $mi-red-100 !important;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .check-details-btn--loading {
    cursor: default;
  }

  .check-details-btn__spinner {
    width: 10px;
    height: 10px;
    border: 2px solid $mi-anthracite-900;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 700ms linear infinite;
  }

  .ambiguous-cell-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0 12px 10px;
  }

  .ambiguous-cell-details__item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .ambiguous-cell-details__value {
    font-weight: 700;
    font-family: "MAN Europe Condensed", sans-serif;
  }

  .q-notification.mi-notification {
    font-weight: 700;
    font-size: 14px;

    .q-notification__icon {
      font-size: 12px !important;
      margin-bottom: auto;
      padding-top: 10px;
      margin-right: 8px !important;
    }

    .mi-icon-close {
      font-size: 10px;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
</style>
